import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {BellRegularIcon} from 'shells/icon/svg/bell-regular-icon';

import type {
    GetMoneyPage_maUrl_automation_actions as AutomationAction,
    ActionInput,
} from 'nutshell-graphql-types';

import {CreateEditNotificationAutomationSidebarForm} from '../create-edit-notification-automation-sidebar-form';
import {useCreateMoneyPageAutomation} from '../graphql/hooks/use-create-money-page-automation';
import {useDeleteMoneyPageAction} from '../graphql/hooks/use-delete-money-page-action';
import {useUpdateMoneyPageAutomation} from '../graphql/hooks/use-update-money-page-automation';

import {AutomationCard} from './automation-card';
import {getSubtitleForAction} from './helpers';

type Props = {
    notificationAction: AutomationAction | null | undefined;
    urlId: string;
};

export function NotificationCardContainer(props: Props) {
    const [
        showCreateEditNotificationAutomationSidebar,
        setShowCreateEditNotificationAutomationSidebar,
    ] = React.useState<boolean>(false);
    const [existingAutomationAction, setExistingAutomationAction] = React.useState<
        AutomationAction | null | undefined
    >(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {createMoneyPageAutomation, isLoading: isSaving} = useCreateMoneyPageAutomation();
    const {updateMoneyPageAutomation, isLoading: isUpdating} = useUpdateMoneyPageAutomation();
    const {deleteMoneyPageAction, isLoading: isDeleting} = useDeleteMoneyPageAction();

    const handleDelete = async (automationActionToDelete: AutomationAction) => {
        await deleteMoneyPageAction({
            actionId: automationActionToDelete.id,
        });
        setShowCreateEditNotificationAutomationSidebar(false);
    };

    const handleSave = (automationActionToSave: ActionInput) => {
        const updateOrCreatePromise = existingAutomationAction
            ? updateMoneyPageAutomation({
                  actionId: existingAutomationAction.id,
                  config: automationActionToSave.config,
              })
            : createMoneyPageAutomation({
                  urlId: props.urlId,
                  action: automationActionToSave,
              });

        updateOrCreatePromise
            .then(() => {
                setShowCreateEditNotificationAutomationSidebar(false);
            })
            .catch((e) => {
                setErrorMessage(safelyGetGraphQLErrorMessage(e) || 'Failed to save');
            });
    };

    return (
        <>
            {props.notificationAction ? (
                <AutomationCard
                    icon={BellRegularIcon}
                    title='Send a notification'
                    subtitle={getSubtitleForAction(props.notificationAction)}
                    onClick={() => {
                        setShowCreateEditNotificationAutomationSidebar(true);
                        setExistingAutomationAction(props.notificationAction);
                        setErrorMessage(null);
                    }}
                />
            ) : (
                <EmptyAddCard
                    onClick={() => {
                        setShowCreateEditNotificationAutomationSidebar(true);
                        setExistingAutomationAction(null);
                        setErrorMessage(null);
                    }}
                    text='Send a notification'
                    background='off-white'
                    size='small'
                    withoutIcon={true}
                    height={60}
                />
            )}
            {showCreateEditNotificationAutomationSidebar ? (
                <CreateEditNotificationAutomationSidebarForm
                    existingAutomationAction={existingAutomationAction}
                    onClose={() => {
                        setShowCreateEditNotificationAutomationSidebar(false);
                    }}
                    handleSave={handleSave}
                    isSaving={isSaving || isUpdating || isDeleting}
                    // @ts-expect-error
                    onDelete={existingAutomationAction ? handleDelete : undefined}
                    saveFailedErrorMessage={errorMessage}
                />
            ) : undefined}
        </>
    );
}
